import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, lastValueFrom, Observable, of, tap } from 'rxjs';
import { NgxPermissionsService } from 'ngx-permissions';
import { CookieService } from 'ngx-cookie-service';
import { BaseClientService } from './base-client.service';
import { HttpClient } from '@angular/common/http';
import { CurrentUser } from '../models/current-user.model';
import { LoginModel, LoginResponse } from '../../features/auth/models/user-auth.models';
import { UserService } from '../../features/users/services/user.service';
import { UserModel } from '../../features/users/models/users.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseClientService<CurrentUser> implements OnDestroy {
  private currentUserSubject = new BehaviorSubject<UserModel | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private cookieService: CookieService,
    private permissionService: NgxPermissionsService,
    private userService: UserService,
    httpClient: HttpClient
  ) {
    super("auth", httpClient);
  }


  async logIn(loginData: LoginModel): Promise<boolean> {
    return new Promise((resolve) => {
      this.create<LoginResponse>(loginData, 'login')
        .subscribe({
          next: (response: LoginResponse) => {
            if (response) {
              this.setToken(response.token);
              this.loadPermissions();
              resolve(true);
            } else {
              resolve(false);
            }
          },
          error: () => {
            resolve(false);
          }
        });
    });
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && token.split('.').length === 3;
  }

  getToken() {
    return localStorage.getItem('token');
  }

  setToken(token: string) {
    localStorage.setItem('token', token);
  }

  logout() {
    this.permissionService.flushPermissions();
    this.cookieService.deleteAll();
    localStorage.clear();
    this.currentUserSubject.next(null);
    window.location.href = window.location.origin + '/login';
  }

  get currentUser(): UserModel | null {
    return this.currentUserSubject.value;
  }

  get isCurrentUserLoaded(): boolean {
    return this.currentUserSubject.value !== null;
  }

  changePassword(data: any) {
    this.create('/reset', data);
  }

  async fetchCurrentUser(): Promise<UserModel | null> {
    try {
      const user = await lastValueFrom(
        this.userService.getCurrentUser().pipe(
          tap(user => {
            this.currentUserSubject.next(user);
          }),
          catchError(err => {
            return of(null);
          })
        )
      );

      return user;
    } catch (error) {
      return null;
    }
  }

  loadPermissions() {
    const token = this.getToken();
    if (!token) return;

    const userData = JSON.parse(
      atob(token
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .split('.')[1]));

    let roles;

    if (Array.isArray(userData.role)) {
      roles = userData.role;
    } else {
      roles = [userData.role];
    }
    this.permissionService.loadPermissions(roles);
  }

  async hasPermission(permission: string): Promise<boolean> {
    this.loadPermissions();
    return await this.permissionService.hasPermission(permission);
  }


  ngOnDestroy(): void {
    this.currentUserSubject.next(null);
    this.currentUserSubject.complete();
  }
}
